import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as colors from '../../../assets/scss/partials/_colors.scss?module';

const EditableValue = ({
    value,
    defaultValue,
    onChange,
    min,
    max,
    suffix = '',
    unit,
    units,
    onUnitChange,
    unsetLabel = '(Standard)',
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value != null ? String(value) : '');
    const inputRef = useRef(null);

    useEffect(() => {
        setTempValue(value != null ? String(value) : '');
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            submit();
        } else if (e.key === 'Escape') {
            setTempValue(value != null ? String(value) : '');
            setIsEditing(false);
        }
    };

    const submit = () => {
        if (tempValue.trim() === '') {
            onChange(undefined);
            setIsEditing(false);
            return;
        }

        let parsed = parseFloat(tempValue);
        if (isNaN(parsed)) {
            parsed = value != null ? value : defaultValue;
        }
        if (min !== undefined && parsed < min) parsed = min;
        if (max !== undefined && parsed > max) parsed = max;

        onChange(parsed);
        setIsEditing(false);
    };

    const isUnset = value == null;

    if (isEditing) {
        return (
            <span className="d-inline-flex align-items-center gap-1">
                <input
                    ref={inputRef}
                    type="number"
                    step="any"
                    value={tempValue}
                    placeholder={defaultValue !== undefined ? String(defaultValue) : ''}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={submit}
                    onKeyDown={handleKeyDown}
                    style={{
                        width: '65px',
                        height: '22px',
                        padding: '0 4px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        textAlign: 'right',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                    }}
                />
                {units && units.length > 0 ? (
                    <select
                        value={unit}
                        onChange={(e) => onUnitChange(e.target.value)}
                        onPointerDown={() => {
                            if (isUnset) {
                                onChange(defaultValue);
                            }
                        }}
                        className="form-select form-select-sm py-0 px-1 border-0 bg-transparent text-muted fw-normal"
                        style={{
                            width: 'auto',
                            minWidth: '38px',
                            height: '20px',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            outline: 'none',
                            boxShadow: 'none',
                            border: 'none',
                            background: 'transparent',
                            padding: '0 12px 0 0',
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%236c757d' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right center',
                            backgroundSize: '8px 8px',
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.color = '#0d6efd';
                            e.target.style.backgroundImage = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%230d6efd' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`;
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.color = '';
                            e.target.style.backgroundImage = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%236c757d' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`;
                        }}
                    >
                        {units.map((u) => (
                            <option key={u} value={u} style={{ background: colors.white, color: colors.dark }}>
                                {u}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span className="text-muted small">{suffix}</span>
                )}
            </span>
        );
    }

    const displayValue = !isUnset ? value : unsetLabel;

    return (
        <span className="d-inline-flex align-items-center gap-1">
            <span
                className={isUnset ? 'text-muted' : 'fw-bold'}
                onClick={() => setIsEditing(true)}
                style={{
                    cursor: 'pointer',
                    borderBottom: isUnset ? '1px dashed #ced4da' : '1px dashed #6c757d',
                    fontStyle: isUnset ? 'italic' : 'normal',
                    paddingBottom: '1px',
                    transition: 'color 0.15s ease, border-color 0.15s ease',
                }}
                title={
                    isUnset
                        ? 'Standardwert aktiv (Klicken zum Anpassen)'
                        : 'Eigener Wert aktiv (Leeren zum Zurücksetzen)'
                }
                onMouseEnter={(e) => {
                    e.target.style.color = '#0d6efd';
                    e.target.style.borderBottomColor = '#0d6efd';
                }}
                onMouseLeave={(e) => {
                    e.target.style.color = '';
                    e.target.style.borderBottomColor = isUnset ? '#ced4da' : '#6c757d';
                }}
            >
                {displayValue}
            </span>
            {units && units.length > 0 ? (
                <select
                    value={unit}
                    onChange={(e) => onUnitChange(e.target.value)}
                    onPointerDown={() => {
                        if (isUnset) {
                            onChange(defaultValue);
                        }
                    }}
                    className="form-select form-select-sm py-0 px-1 border-0 bg-transparent text-muted fw-normal"
                    style={{
                        width: 'auto',
                        minWidth: '38px',
                        height: '20px',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: 'none',
                        border: 'none',
                        background: 'transparent',
                        padding: '0 12px 0 0',
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%236c757d' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right center',
                        backgroundSize: '8px 8px',
                        opacity: isUnset ? 0.6 : 1,
                        transition: 'opacity 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.color = '#0d6efd';
                        e.target.style.backgroundImage = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%230d6efd' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`;
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.color = '';
                        e.target.style.backgroundImage = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%236c757d' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`;
                    }}
                >
                    {units.map((u) => (
                        <option key={u} value={u} style={{ background: colors.white, color: colors.dark }}>
                            {u}
                        </option>
                    ))}
                </select>
            ) : (
                <span className="text-muted small">{suffix}</span>
            )}
        </span>
    );
};

EditableValue.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    suffix: PropTypes.string,
    unit: PropTypes.string,
    units: PropTypes.arrayOf(PropTypes.string),
    onUnitChange: PropTypes.func,
    unsetLabel: PropTypes.string,
};

export default EditableValue;
